import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Users, ClockIcon, CalendarDays, Wallet, Briefcase,
  TrendingUp, GraduationCap, Laptop2, HeadphonesIcon, FileText, BarChart3,
  Settings, LogOut, Building2, Bell, Search, ChevronRight, ClipboardList, Award,
  Network, UsersRound, Target, BookOpen, Plane, Shield
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, live: true },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/employees", label: "Employees", icon: Users, live: true },
      { to: "/hierarchy", label: "Hierarchy", icon: Network, live: true },
      { to: "/team", label: "My Team", icon: UsersRound, live: true },
      { to: "/recruitment", label: "Recruitment", icon: Briefcase, live: true },
    ],
  },
  {
    label: "Time & Pay",
    items: [
      { to: "/attendance", label: "Attendance", icon: ClockIcon, live: true },
      { to: "/leave", label: "Leave", icon: CalendarDays, live: true },
      { to: "/payroll", label: "Payroll", icon: Wallet, live: true },
      { to: "/expenses", label: "Expenses", icon: Plane, live: true },
    ],
  },
  {
    label: "Delivery",
    items: [
      { to: "/dwr", label: "DWR & Projects", icon: ClipboardList, live: true },
      { to: "/performance", label: "Performance", icon: TrendingUp, live: true },
      { to: "/kpi", label: "KPI Scores", icon: Target, live: true },
      { to: "/certifications", label: "Certifications", icon: Award, live: true },
      { to: "/training", label: "Training", icon: GraduationCap, live: true },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/assets", label: "Assets", icon: Laptop2, live: true },
      { to: "/helpdesk", label: "Helpdesk", icon: HeadphonesIcon, live: true },
      { to: "/documents", label: "Documents & Policy", icon: FileText, live: true },
      { to: "/reports", label: "Reports", icon: BarChart3, live: true },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/security", label: "Security", icon: Shield, live: true },
      { to: "/settings", label: "Settings", icon: Settings, live: true, adminOnly: true },
    ],
  },
];

function initials(name = "") {
  return name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-slate-900/40 bg-[#0F172A] sticky top-0 h-screen text-slate-200">
        <div className="h-16 px-5 flex items-center gap-3 border-b border-slate-700/60">
          <img src="/logo.png" alt="Techtreno" className="h-9 w-9 object-contain" />
          <div className="min-w-0">
            <div className="font-display font-bold text-white text-sm leading-tight truncate">Techtreno</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">HRMS</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-4">
          {NAV_GROUPS.map(group => {
            const visible = group.items.filter(item => !item.adminOnly || user?.role === "admin");
            if (visible.length === 0) return null;
            return (
              <div key={group.label} className="space-y-0.5">
                <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  {group.label}
                </div>
                {visible.map(({ to, label, icon: Icon, live }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    onClick={(e) => { if (!live) { e.preventDefault(); }}}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all
                      ${isActive
                        ? "bg-[#DC2626]/15 text-white ring-1 ring-[#DC2626]/40"
                        : live
                          ? "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                          : "text-slate-500 cursor-not-allowed"}`
                    }
                    data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                    <span className="flex-1">{label}</span>
                    {!live && <span className="text-[9px] uppercase tracking-wider bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded">Soon</span>}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-700/60">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              {user?.avatar_url && <AvatarImage src={`${API}${user.avatar_url}`} alt={user?.name} />}
              <AvatarFallback className="bg-[#DC2626]/20 text-[#FCA5A5] text-xs font-semibold">
                {initials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400">{user?.role}</div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-red-600/20 transition-all"
              data-testid="logout-btn"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Top header */}
        <header className="sticky top-0 z-30 h-16 border-b border-slate-200/70 backdrop-blur-xl bg-white/80 flex items-center gap-4 px-4 sm:px-8">
          <div className="lg:hidden flex items-center gap-2">
            <img src="/logo.png" alt="Techtreno" className="h-14 w-14 object-contain drop-shadow-lg" />
            <span className="font-display font-bold text-slate-900">Techtreno</span>
          </div>
          <div className="flex-1 max-w-md relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
            <Input placeholder="Search employees, tickets, documents…" className="pl-9 h-10 bg-slate-50 border-slate-200" data-testid="global-search" />
          </div>
          <button className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 relative" data-testid="notifications-btn">
            <Bell className="h-5 w-5" strokeWidth={1.5} />
            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[#DC2626]" />
          </button>
          <div className="hidden sm:flex items-center gap-2 text-xs">
            <span className="text-slate-500">Role</span>
            <span className="px-2 py-1 rounded-md bg-red-50 text-[#DC2626] font-semibold uppercase tracking-wider">{user?.role}</span>
          </div>
        </header>
        <div className="flex-1 p-4 sm:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function Crumb({ items }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-3">
      {items.map((it, i) => (
        <span key={`${it}-${i}`} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="h-3 w-3" strokeWidth={1.5} />}
          <span className={i === items.length - 1 ? "text-slate-900 font-medium" : ""}>{it}</span>
        </span>
      ))}
    </div>
  );
}
